import { authGate } from './core';
import { useAuthStore } from '../../stores/authStore';
import {
  appendReqTimestamp,
  countAllTabsReqTimestamps,
  OWN_REQ_KEY,
} from './storageSync';

const DEFAULT_WINDOW_MS = 10_000;

// SlidingWindowLedger — localStorage-backed (persistent across reloads) via storageSync.
// In-memory array for fast local reads; cross-tab size() aggregates all tabs.
export class SlidingWindowLedger {
  private memTimestamps: number[] = [];
  private readonly windowMs: number;

  constructor(windowMs: number = DEFAULT_WINDOW_MS) {
    this.windowMs = windowMs;
    // Bootstrap: restore this tab's own persisted timestamps
    try {
      const raw = localStorage.getItem(OWN_REQ_KEY);
      if (raw) {
        this.memTimestamps = JSON.parse(raw) as number[];
      }
    } catch {
      // fail-safe: start with empty memory
    }
  }

  append(ts: number): void {
    this.memTimestamps.push(ts);
    appendReqTimestamp(ts, this.windowMs);
  }

  pruneOlderThan(windowMs: number, now: number = Date.now()): void {
    const cutoff = now - windowMs;
    this.memTimestamps = this.memTimestamps.filter(t => t >= cutoff);
  }

  // Cross-tab aware count (reads all tab keys from localStorage)
  size(now: number = Date.now()): number {
    return countAllTabsReqTimestamps(this.windowMs, now);
  }

  // Local-only count (fast, no localStorage scan)
  localSize(now: number = Date.now()): number {
    const cutoff = now - this.windowMs;
    return this.memTimestamps.filter(t => t >= cutoff).length;
  }
}

// GuardBroadcaster — BroadcastChannel('apiGuard') implementation.
// Source of truth = localStorage; BC is a real-time cross-tab overlay.
// Falls back to no-op silently on old Safari (no BroadcastChannel support).
export class GuardBroadcaster {
  private bc: BroadcastChannel | null = null;
  private readonly handlers = new Map<string, Array<(data: unknown) => void>>();

  constructor() {
    try {
      this.bc = new BroadcastChannel('apiGuard');
      this.bc.onmessage = (e: MessageEvent<{ event: string; data?: unknown }>) => {
        const { event: evtName, data } = e.data ?? {};
        if (!evtName) return;
        this.handlers.get(evtName)?.forEach(h => {
          try { h(data); } catch {}
        });
      };
    } catch {
      // BroadcastChannel unavailable — no-op fallback (storage event path still works)
    }
  }

  publish(event: string, data?: unknown): void {
    try {
      this.bc?.postMessage({ event, data });
    } catch {}
  }

  subscribe(event: string, handler: (data: unknown) => void): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }
    this.handlers.get(event)!.push(handler);
    return () => {
      const list = this.handlers.get(event);
      if (!list) return;
      const idx = list.indexOf(handler);
      if (idx >= 0) list.splice(idx, 1);
    };
  }
}

// Module singletons
export const reqLedger = new SlidingWindowLedger();
export const guardBroadcaster = new GuardBroadcaster();

// Wire: other tabs' auth_stopped BC event → this tab's authGate + zustand
// (storage event path in storageSync.ts covers the slow path; BC is the fast path)
guardBroadcaster.subscribe('auth_stopped', () => {
  try {
    authGate.markStopped();
    useAuthStore.getState().setSessionExpired(true);
  } catch {}
});
